import { useState } from "react"
import { useParams } from "react-router-dom"
import { isGuestThread } from "@/lib/authGate"
import { track } from "@/lib/analytics"
import { sendVerification } from "../api"
import type { Source } from "../contract"
import { useTurnStore } from "../store"

type Verdict = "confirmed" | "contradicted"
type Phase = "choose" | "no" | "saving" | "saved"

/** "Is this result correct?" for a case: the reader's vote is recorded against the case in Nira. */
export function ResultVote({ src, turnKey }: { src: Source; turnKey: string }) {
  const { threadId = "" } = useParams<{ threadId: string }>()
  const turnId = useTurnStore((s) => s.turns[turnKey]?.turnId ?? "")
  const [phase, setPhase] = useState<Phase>("choose")
  const [note, setNote] = useState("")
  const [failed, setFailed] = useState(false)
  if (src.kind !== "case" || !src.doc_id || !threadId || !turnId || isGuestThread(threadId)) return null

  const vote = async (verdict: Verdict, typed?: string) => {
    setPhase("saving")
    setFailed(false)
    const clean = typed?.trim()
    try {
      await sendVerification(threadId, {
        turn_id: turnId,
        source_id: src.id,
        verdict,
        ...(clean ? { note: clean } : {}),
      })
      track("result_voted", { verdict })
      setPhase("saved")
    } catch {
      setFailed(true)
      setPhase(verdict === "contradicted" ? "no" : "choose")
    }
  }

  const change = () => {
    setNote("")
    setFailed(false)
    setPhase("choose")
  }

  return (
    <section className="rv" aria-label="Is this result correct?">
      <p className="lab">Is this result correct?</p>
      {phase === "saving" ? (
        <p className="spe" role="status">Saving…</p>
      ) : phase === "saved" ? (
        <p className="spe rv-thanks" role="status">
          Thank you. This helps every user.{" "}
          <button type="button" className="rv-link" onClick={change}>
            Change my answer
          </button>
        </p>
      ) : phase === "no" ? (
        <form
          className="rv-note"
          onSubmit={(e) => {
            e.preventDefault()
            void vote("contradicted", note)
          }}
        >
          <label className="rv-q" htmlFor={`rv-note-${src.id}`}>
            What is wrong? <span>Optional</span>
          </label>
          <textarea
            id={`rv-note-${src.id}`}
            value={note}
            maxLength={500}
            rows={3}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="rv-row">
            <button className="nr-btn" data-variant="primary" data-size="sm" type="submit">Send</button>
            <button className="nr-btn" data-variant="quiet" data-size="sm" type="button" onClick={() => void vote("contradicted")}>Skip</button>
            <button className="nr-btn" data-variant="quiet" data-size="sm" type="button" onClick={() => setPhase("choose")}>Back</button>
          </div>
        </form>
      ) : (
        <div className="rv-row" role="group" aria-label="Is this result correct?">
          <button type="button" className="nr-btn" data-size="sm" onClick={() => void vote("confirmed")}>Yes</button>
          <button type="button" className="nr-btn" data-size="sm" onClick={() => setPhase("no")}>No</button>
        </div>
      )}
      {failed ? <p className="spe rv-fail" role="alert">Could not save right now. Try again.</p> : null}
    </section>
  )
}
